import sortBy from "lodash-es/sortBy";
import uniq from "lodash-es/uniq";
import IDream from "../Entity/IDream";
import { dreamService } from "./DreamService";
import { IDataProvider } from "motw_core/Data/IDataProvider";
import {
    hashTag_p_boredom,
    hashTag_thisweek,
} from "motw_core/Constants/constants";

const HashTagsService = () => {
    const HashTagsFilter = (word: string) => IsWordHashTag(word);

    const IsWordHashTag = (word: string) => word[0] === `#`;

    function GetAllDreamHashTags(dreamRows: IDream[]) {
        if (dreamRows.length === 0) {
            return [];
        }

        const hashTags = dreamRows
            .map((dream) => dream.name)
            .map((dreamName) => dreamName.split(` `))
            .reduce((wordArray1, wordArray2) => wordArray1.concat(wordArray2))
            .filter(HashTagsFilter)
            .concat([hashTag_thisweek, hashTag_p_boredom]);

        const sortedHashTags = sortBy(hashTags, (hashTag) => hashTag);
        const uniqueHashTags = uniq(sortedHashTags);
        return uniqueHashTags;
    }

    function GetAllHashTagsInDream(dream: IDream) {
        const hashTags = dream.name.split(` `).filter(HashTagsFilter);
        const sortedHashTags = sortBy(hashTags, (hashTag) => hashTag);
        const uniqueHashTags = uniq(sortedHashTags);
        return uniqueHashTags;
    }

    const IsEnergyHashTag = (hashTag: string) => /#\d_.*/.test(hashTag);

    const IsProblemHashTag = (hashTag: string) => /#p_.*/.test(hashTag);

    const IsCampaignHashTag = (hashTag: string) => /#_.*/.test(hashTag);

    const DoesDreamHaveHashTag = (dream: IDream, hashTag: string) =>
        dream.name.split(` `).filter((word) => word === hashTag).length > 0;

    const DoesDreamHaveAnyProblemHashTags = (dream: IDream) => {
        const hashTagsInDream = GetAllHashTagsInDream(dream);

        const problemHashTags = hashTagsInDream.filter((hashTag) =>
            IsProblemHashTag(hashTag)
        );

        const doesDreamHaveProblemHashTag = problemHashTags.length > 0;
        return doesDreamHaveProblemHashTag;
    };

    const DoesDreamHaveAnyHashTags = (dream: IDream) =>
        dream.name.split(` `).filter(HashTagsFilter).length > 0;

const GetStringWithHashTag = (input: string, hashTag: string) =>
        `${input} ${hashTag}`;

    const AddHashTagToDreamAsync = async (
        dream: IDream,
        hashTag: string,
        dataProvider: IDataProvider
    ) => {
        const newDreamName = GetStringWithHashTag(dream.name, hashTag);

        const isresonating =
            hashTag === hashTag_thisweek ? false : dream.isresonating;

        const updatedDream: IDream = {
            ...dream,
            name: newDreamName,
            isresonating,
        };

        await dreamService.UpdateDreamAsync(updatedDream, dataProvider);
    };

    const GetStringWithoutHashTag = (input: string, hashTag: string) =>
        input
            .split(` `)
            .filter((word) => word !== hashTag)
            .join(` `)
            .trimEnd();

    const RemoveHashTagFromDreamAsync = async (
        dream: IDream,
        hashTag: string,
        dataProvider: IDataProvider
    ) => {
        const newDreamName = GetStringWithoutHashTag(dream.name, hashTag);
        const updatedDream = { ...dream, name: newDreamName };
        await dreamService.UpdateDreamAsync(updatedDream, dataProvider);
    };

    const RenameHashTagAsync = async (
        dreams: IDream[],
        currentHashTag: string,
        newHashTag: string,
        dataProvider: IDataProvider
    ) => {
        const dreamsToChange = dreams.filter((dream) =>
            DoesDreamHaveHashTag(dream, currentHashTag)
        );

        await RemoveHashTagAsync(dreams, currentHashTag, dataProvider);

        for (let dreamToUpdate of dreamsToChange) {
            const newDreamName = GetStringWithoutHashTag(
                dreamToUpdate.name,
                currentHashTag
            );

            const updatedDream = { ...dreamToUpdate, name: newDreamName };

            await AddHashTagToDreamAsync(
                updatedDream,
                newHashTag,
                dataProvider
            );
        }
    };

    const RemoveHashTagAsync = async (
        dreams: IDream[],
        hashTag: string,
        dataProvider: IDataProvider
    ) => {
        const dreamsToChange = dreams.filter((dream) =>
            DoesDreamHaveHashTag(dream, hashTag)
        );

        for (let dreamToUpdate of dreamsToChange) {
            await RemoveHashTagFromDreamAsync(
                dreamToUpdate,
                hashTag,
                dataProvider
            );
        }
    };

    return {
        HashTagsFilter,
        IsWordHashTag,
        GetAllDreamHashTags,
        GetAllHashTagsInDream,
        IsEnergyHashTag,
        IsProblemHashTag,
        IsCampaignHashTag,
        DoesDreamHaveHashTag,
        DoesDreamHaveAnyProblemHashTags,
        DoesDreamHaveAnyHashTags,
        GetStringWithHashTag,
        AddHashTagToDreamAsync,
        GetStringWithoutHashTag,
        RemoveHashTagFromDreamAsync,
        RenameHashTagAsync,
        RemoveHashTagAsync,
    };
};

export const hashTagsService = HashTagsService();
